// src/components/admin/company/CompanySettings.jsx
import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { useNotification } from '../../../context/NotificationContext';
import { useImageUpload } from '../../../hooks/useImageUpload';
import Loading from '../../common/Loading';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { FiUpload, FiSave, FiImage } from 'react-icons/fi';

const CompanySettings = () => {
  const [companyData, setCompanyData] = useState({
    company_name: '',
    about_company: '',
    mission: '',
    vision: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    website: '',
    social_media: {
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: ''
    }
  });

  const [ceoData, setCeoData] = useState({
    ceo_name: '',
    ceo_bio: '',
    ceo_avatar: null
  });

  const { request, loading } = useApi();
  const { showNotification } = useNotification();
  const { uploadImage, uploading } = useImageUpload();

  useEffect(() => {
    fetchCompanyData();
    fetchCeoData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const response = await request('/company/profile');
      if (response) {
        setCompanyData({
          company_name: response.company_name || '',
          about_company: response.about_company || '',
          mission: response.mission || '',
          vision: response.vision || '',
          email: response.email || '',
          phone: response.phone || '',
          whatsapp: response.whatsapp || '',
          address: response.address || '',
          website: response.website || '',
          social_media: {
            facebook: response.social_media?.facebook || '',
            instagram: response.social_media?.instagram || '',
            twitter: response.social_media?.twitter || '',
            youtube: response.social_media?.youtube || ''
          }
        });
      }
    } catch (error) {
      showNotification('Error mengambil data perusahaan', 'error');
    }
  };

  const fetchCeoData = async () => {
    try {
      const response = await request('/company/ceo');
      if (response) {
        setCeoData({
          ceo_name: response.ceo_name || '',
          ceo_bio: response.ceo_bio || '',
          ceo_avatar: response.ceo_avatar || null
        });
      }
    } catch (error) {
      showNotification('Error mengambil data CEO', 'error');
    }
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('social_')) {
      const socialKey = name.replace('social_', '');
      setCompanyData(prev => ({
        ...prev,
        social_media: {
          ...prev.social_media,
          [socialKey]: value
        }
      }));
    } else {
      setCompanyData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCeoChange = (e) => {
    const { name, value } = e.target;
    setCeoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarUpload = async (file) => {
    try {
      const imageUrl = await uploadImage(file);
      setCeoData(prev => ({
        ...prev,
        ceo_avatar: imageUrl
      }));
      showNotification('Avatar berhasil diupload', 'success');
    } catch (error) {
      showNotification('Error upload avatar', 'error');
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      await request('/company/profile', {
        method: 'PUT',
        data: companyData
      });
      showNotification('Data perusahaan berhasil disimpan', 'success');
    } catch (error) {
      showNotification('Error menyimpan data perusahaan', 'error');
    }
  };

  const handleCeoSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('ceo_name', ceoData.ceo_name);
      formData.append('ceo_bio', ceoData.ceo_bio);
      if (ceoData.ceo_avatar instanceof File) {
        formData.append('ceo_avatar', ceoData.ceo_avatar);
      }

      await request('/company/ceo', {
        method: 'PUT',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      showNotification('Data CEO berhasil disimpan', 'success');
    } catch (error) {
      showNotification('Error menyimpan data CEO', 'error');
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pengaturan Perusahaan</h1>
        <p className="text-gray-600">Kelola informasi profil perusahaan dan CEO</p>
      </div>

      {/* Company Profile Form */}
      <form onSubmit={handleCompanySubmit} className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Profil Perusahaan</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Nama Perusahaan"
            name="company_name"
            value={companyData.company_name}
            onChange={handleCompanyChange}
            required
          />
          
          <Input
            label="Email"
            name="email"
            type="email"
            value={companyData.email}
            onChange={handleCompanyChange}
            required
          />
          
          <Input
            label="Nomor Telepon"
            name="phone"
            value={companyData.phone}
            onChange={handleCompanyChange}
            required
          />
          
          <Input
            label="WhatsApp"
            name="whatsapp"
            value={companyData.whatsapp}
            onChange={handleCompanyChange}
          />
          
          <Input
            label="Website"
            name="website"
            type="url"
            value={companyData.website}
            onChange={handleCompanyChange}
          />
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alamat
          </label>
          <textarea
            name="address"
            value={companyData.address}
            onChange={handleCompanyChange}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tentang Perusahaan
          </label>
          <textarea
            name="about_company"
            value={companyData.about_company}
            onChange={handleCompanyChange}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Misi
            </label>
            <textarea
              name="mission"
              value={companyData.mission}
              onChange={handleCompanyChange}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visi
            </label>
            <textarea
              name="vision"
              value={companyData.vision}
              onChange={handleCompanyChange}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Social Media */}
        <div className="mt-6">
          <h3 className="text-md font-medium text-gray-900 mb-4">Media Sosial</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Facebook"
              name="social_facebook"
              value={companyData.social_media.facebook}
              onChange={handleCompanyChange}
              placeholder="https://facebook.com/username"
            />
            
            <Input
              label="Instagram"
              name="social_instagram"
              value={companyData.social_media.instagram}
              onChange={handleCompanyChange}
              placeholder="https://instagram.com/username"
            />
            
            <Input
              label="Twitter"
              name="social_twitter"
              value={companyData.social_media.twitter}
              onChange={handleCompanyChange}
              placeholder="https://twitter.com/username"
            />
            
            <Input
              label="YouTube"
              name="social_youtube"
              value={companyData.social_media.youtube}
              onChange={handleCompanyChange}
              placeholder="https://youtube.com/channel/..."
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button type="submit" disabled={loading}>
            <FiSave className="h-4 w-4 mr-2" />
            Simpan Profil Perusahaan
          </Button>
        </div>
      </form>

      {/* CEO Profile Form */}
      <form onSubmit={handleCeoSubmit} className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Profil CEO</h2>
        
        {/* CEO Avatar */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Foto CEO
          </label>
          <div className="flex items-center gap-4">
            {ceoData.ceo_avatar && (
              <img
                src={typeof ceoData.ceo_avatar === 'string' 
                  ? ceoData.ceo_avatar 
                  : URL.createObjectURL(ceoData.ceo_avatar)
                }
                alt="CEO Avatar"
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
              />
            )}
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setCeoData(prev => ({ ...prev, ceo_avatar: file }));
                  }
                }}
                className="hidden"
                id="ceo-avatar-upload"
              />
              <label
                htmlFor="ceo-avatar-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiUpload className="h-4 w-4 mr-2" />
                Upload Foto
              </label>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Nama CEO"
            name="ceo_name"
            value={ceoData.ceo_name}
            onChange={handleCeoChange}
            required
          />
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Biografi CEO
          </label>
          <textarea
            name="ceo_bio"
            value={ceoData.ceo_bio}
            onChange={handleCeoChange}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Tulis biografi singkat CEO..."
            required
          />
        </div>

        <div className="flex justify-end mt-6">
          <Button type="submit" disabled={loading || uploading}>
            <FiSave className="h-4 w-4 mr-2" />
            {uploading ? 'Mengupload...' : 'Simpan Profil CEO'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanySettings;